import React from 'react';
import './SlideContent.css';
import { RenderMarkdown } from '../chat/RenderMarkdown';
import { FaSync, FaSpinner } from 'react-icons/fa';
import useWindowSize from '../hooks/useWindowSize'; // Import the hook

// Helper to safely handle potentially undefined or null values
const safeguardValue = (value) => value || '';

// Reusable Reset Button Component
const ResetButton = ({ slideId, contentKey, dimension, onResetDimension, isResettingThisSlide }) => {
  const { width } = useWindowSize(); // Call hook at the top
  const isMobile = width <= 768;

  if (!onResetDimension || !slideId) return null;

  return (
    <button
      onClick={() => onResetDimension(slideId)}
      title={`Reset this section (${contentKey || dimension})`}
      disabled={isResettingThisSlide}
      className="reset-section-button" // Added class for styling
    >
      {isResettingThisSlide ? (
        <FaSpinner className="spinner-small-button" />
      ) : (
        <FaSync />
      )}
      {!isMobile && (
        <span style={{ marginLeft: '8px' }}>
          {isResettingThisSlide ? 'Resetting...' : 'Reset Section'}
        </span>
      )}
    </button>
  );
};

const SlideContent = ({ slide, onResetDimension, isResettingThisSlide }) => {
  // A helper function to create content elements with common props
  const createContentElement = (ContentComponent) => {
    const resetProps = {
      slideId: slide.id,
      contentKey: slide.contentKey,
      dimension: slide.dimension,
      onResetDimension,
      isResettingThisSlide,
    };
    return <ContentComponent {...slide} {...resetProps} />;
  };

  return (
    <div className="slide-container">
      {(() => {
        switch (slide.dimension) {
          case 'Objectives':
            return createContentElement(ObjectivesContent);
          case 'Explore':
            return createContentElement(ExploreContent);
          case 'Compare':
            return createContentElement(CompareContent);
          case 'Question':
            return createContentElement(QuestionContent);
          case 'Connect':
            return createContentElement(ConnectContent);
          case 'Appreciate':
            return createContentElement(AppreciateContent);
          case 'Activities':
            return createContentElement(ActivitiesContent);
          default:
            return null;
        }
      })()}
    </div>
  );
};


const DimensionHeader = ({ title, ...resetProps }) => (
  <div className="dimension-header">
    <h2>{title}</h2>
    <ResetButton {...resetProps} />
  </div>
);

const ObjectivesContent = ({ learningObjectives = {}, ...resetProps }) => (
  <div className="slide objectives-content">
    <DimensionHeader title="Learning Objectives" {...resetProps} />
    {Object.entries(learningObjectives).map(([category, objectives]) => (
      <div key={category} className="content-section">
        <h3>{capitalizeWords(category)}</h3>
        <ul>
          {objectives.map((objective, index) => (
            <li key={index}><RenderMarkdown markdown={safeguardValue(objective)} /></li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const ActivitiesContent = ({ activities = [], ...resetProps }) => (
  <div className="slide activities-content">
    <DimensionHeader title="Activities" {...resetProps} />
    {activities.map((activity, index) => (
      <div key={index} className="activity-section">
        <h3>{activity.title || 'Activity'}</h3>
        <p><strong>Dimension:</strong> {activity.dimension || ''}</p>
        <p><strong>Objective:</strong> <RenderMarkdown markdown={safeguardValue(activity.objective)} /></p>
        <div className="content-section">
          <h4>Materials</h4>
          <ul>
            {(activity.materials || []).map((material, idx) => (
              <li key={idx}><RenderMarkdown markdown={safeguardValue(material)} /></li>
            ))}
          </ul>
        </div>
        <div className="content-section">
          <h4>Instructions</h4>
          <ol>
            {(activity.instructions || []).map((instruction, idx) => (
              <li key={idx}><RenderMarkdown markdown={safeguardValue(instruction)} /></li>
            ))}
          </ol>
        </div>
      </div>
    ))}
  </div>
);

const ExploreContent = ({ content = '', explanation = '', observations = '', fascinatingFacts = '', ...resetProps }) => (
  <div className="slide explore-content">
    <DimensionHeader title="Explore" {...resetProps} />
    {content && (
      <div className="content-section">
        <h3>Content</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(content)} />
        </div>
      </div>
    )}
    {explanation && (
      <div className="content-section">
        <h3>Explanation</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(explanation)} />
        </div>
      </div>
    )}
    {observations && (
      <div className="content-section">
        <h3>Observations</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(observations)} />
        </div>
      </div>
    )}
    {fascinatingFacts && (
      <div className="content-section">
        <h3>Fascinating Facts</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(fascinatingFacts)} />
        </div>
      </div>
    )}
  </div>
);

const CompareContent = ({ analogy = '', content = '', explanation = '', comparison = '', ...resetProps }) => (
  <div className="slide compare-content">
    <DimensionHeader title="Compare" {...resetProps} />
    {analogy && (
      <div className="content-section">
        <h3>Analogy</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(analogy)} />
        </div>
      </div>
    )}
    {content && (
      <div className="content-section">
        <h3>Content</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(content)} />
        </div>
      </div>
    )}
    {explanation && (
      <div className="content-section">
        <h3>Explanation</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(explanation)} />
        </div>
      </div>
    )}
    {comparison && (
      <div className="content-section">
        <h3>Comparison</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(comparison)} />
        </div>
      </div>
    )}
  </div>
);

const capitalizeWords = (text) => {
  if (!text || typeof text !== 'string') return ''; // Safeguard for non-string inputs
  return text
    .replace(/_/g, ' ') 
    .split(' ') 
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) 
    .join(' '); 
};

const titleSubtopicMap = {
  Explore: ['Content', 'Explanation', 'Observations', 'Fascinating Facts'],
  Compare: ['Analogy', 'Content', 'Explanation', 'Comparison'],
  Question: [
    'Negation of Chance',
    'Negation of Material Causes',
    'Negation of Nature',
    'Conclusion',
  ],
  Connect: [ 'Interconnectedness','Interdependency', "Allah's Names", 'Analogical Reflection', 'Questions for Deeper Connection', 'Contemplation and Appreciation'], // Added missing ones
  Appreciate: ['What ifs', 'Zikr Fikr Shukr', 'Character Lessons', 'Connect With Quran', 'Connect With Hadith', 'Dua'], // Added Dua
};


const QuestionContent = ({
  questions = [],
  conclusion = '',
  subtopicIndex = 0,
  ...resetProps
}) => {
  const subtopicTitle = titleSubtopicMap.Question[subtopicIndex] || 'Question Section';

  return (
    <div className="slide question-content">
      <DimensionHeader title="Question" {...resetProps} />

      {subtopicIndex !== 3 && questions && questions.length > 0 && (
        <div className="content-section">
          <h3>{subtopicTitle}</h3>
          <ul>
            {questions.map((q, i) => (
              <li key={i}><RenderMarkdown markdown={safeguardValue(q)} /></li>
            ))}
          </ul>
        </div>
      )}

      {subtopicIndex === 3 && conclusion && (
        <div className="content-section">
          <h3>Conclusion</h3>
          <div className="markdown-content">
            <RenderMarkdown markdown={safeguardValue(conclusion)} />
          </div>
        </div>
      )}
    </div>
  );
};


const ConnectContent = ({
  contentKey = '',
  interconnectedness = '',
  interdependency = '',
  completeDependency = '',
  allahNames = {},
  analogicalReflection = '', 
  questionsForDeeperConnection = '',
  contemplationAndAppreciation = '',
  subtopicIndex = 0,
  ...resetProps
}) => {
  const normalizedAllahNames = {
    namesInEnglish: Array.isArray(allahNames?.namesInEnglish)
      ? allahNames.namesInEnglish
      : Array.isArray(allahNames?.names_in_english)
        ? allahNames.names_in_english
        : [],
    namesInArabic: Array.isArray(allahNames?.namesInArabic)
      ? allahNames.namesInArabic
      : Array.isArray(allahNames?.names_in_arabic)
        ? allahNames.names_in_arabic
        : [],
    whatItTells: Array.isArray(allahNames?.whatItTells)
      ? allahNames.whatItTells
      : Array.isArray(allahNames?.what_it_tells)
        ? allahNames.what_it_tells
        : [],
  };

  const connectTitleByKey = {
    interconnectedness: 'Interconnectedness',
    interdependency: 'Interdependency',
    completeDependency: 'Complete Dependency',
    allahNames: "Allah's Names",
    analogicalReflection: 'Analogical Reflection',
    questionsForDeeperConnection: 'Questions for Deeper Connection',
    contemplationAndAppreciation: 'Contemplation and Appreciation',
  };

  const sectionKey = contentKey || ['interconnectedness', 'interdependency', 'allahNames', 'analogicalReflection', 'questionsForDeeperConnection', 'contemplationAndAppreciation'][subtopicIndex];
  const subtopicTitle = connectTitleByKey[sectionKey] || titleSubtopicMap.Connect[subtopicIndex] || 'Connect Section';
  const interdependencyValue = interdependency || completeDependency;

  const hasAllahNamesRows =
    normalizedAllahNames.whatItTells.length > 0 ||
    normalizedAllahNames.namesInEnglish.length > 0 ||
    normalizedAllahNames.namesInArabic.length > 0;

  const rowCount = Math.max(
    normalizedAllahNames.whatItTells.length,
    normalizedAllahNames.namesInEnglish.length,
    normalizedAllahNames.namesInArabic.length
  );
  const rows = Array.from({ length: rowCount }, (_, i) => ({
    whatItTells: normalizedAllahNames.whatItTells[i] || '',
    namesInEnglish: normalizedAllahNames.namesInEnglish[i] || '',
    namesInArabic: normalizedAllahNames.namesInArabic[i] || '',
  }));

  return (
    <div className="slide connect-content">
      <DimensionHeader title="Connect" {...resetProps} />
      {sectionKey === 'interconnectedness' && interconnectedness && (
        <div className="content-section">
          <h3>{subtopicTitle}</h3>
          <div className="markdown-content">
            <RenderMarkdown markdown={safeguardValue(interconnectedness)} />
          </div>
        </div>
      )}

      {(sectionKey === 'interdependency' || sectionKey === 'completeDependency') && interdependencyValue && (
        <div className="content-section">
          <h3>{subtopicTitle}</h3>
          <div className="markdown-content">
            <RenderMarkdown markdown={safeguardValue(interdependencyValue)} />
          </div>
        </div>
      )}

      {sectionKey === 'allahNames' && hasAllahNamesRows && (
        <div className="content-section">
          <h3>{subtopicTitle}</h3>
          <table>
            <thead>
              <tr>
                <th>What it tells us</th>
                <th>Names in English</th>
                <th>Names in Arabic</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td><RenderMarkdown markdown={safeguardValue(row.whatItTells)} /></td>
                  <td><RenderMarkdown markdown={safeguardValue(row.namesInEnglish)} /></td>
                  <td><RenderMarkdown markdown={safeguardValue(row.namesInArabic)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Added rendering for new Connect subtopics */}
      {sectionKey === 'analogicalReflection' && analogicalReflection && (
         <div className="content-section">
           <h3>{subtopicTitle}</h3>
           <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(analogicalReflection)} /></div>
         </div>
      )}
      {sectionKey === 'questionsForDeeperConnection' && questionsForDeeperConnection && (
         <div className="content-section">
           <h3>{subtopicTitle}</h3>
           <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(questionsForDeeperConnection)} /></div>
         </div>
      )}
      {sectionKey === 'contemplationAndAppreciation' && contemplationAndAppreciation && (
         <div className="content-section">
           <h3>{subtopicTitle}</h3>
           <div className="markdown-content"><RenderMarkdown markdown={safeguardValue(contemplationAndAppreciation)} /></div>
          </div>
      )}
    </div>
  );
};


const AppreciateContent = ({
  whatIfs = '',
  zikrFikrShukr = {},
  characterLessons = '',
  connectWithQuran = [],
  connectWithHadith = [],
  dua = '',
  ...resetProps
}) => (
  <div className="slide appreciate-content">
    <DimensionHeader title="Appreciate" {...resetProps} />
    {whatIfs && (
      <div className="content-section">
        <h3>What Ifs</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(whatIfs)} />
        </div>
      </div>
    )}

    {zikrFikrShukr && 
      zikrFikrShukr.zikr && zikrFikrShukr.zikr.length > 0 &&
      zikrFikrShukr.fikr && zikrFikrShukr.fikr.length > 0 &&
      zikrFikrShukr.shukr && zikrFikrShukr.shukr.length > 0 && (
        <div className="content-section">
          <h3>Zikr, Fikr, Shukr</h3>
          <table>
            <thead>
              <tr>
                <th>Zikr</th>
                <th>Fikr</th>
                <th>Shukr</th>
              </tr>
            </thead>
            <tbody>
              {zikrFikrShukr.zikr.map((zikrItem, index) => (
                <tr key={index}>
                  <td><RenderMarkdown markdown={safeguardValue(zikrItem)} /></td>
                  <td><RenderMarkdown markdown={safeguardValue(zikrFikrShukr.fikr[index])} /></td>
                  <td><RenderMarkdown markdown={safeguardValue(zikrFikrShukr.shukr[index])} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    {characterLessons && (
      <div className="content-section">
        <h3>Character Lessons</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(characterLessons)} />
        </div>
      </div>
    )}

    {connectWithQuran && connectWithQuran.length > 0 && (
      <div className="content-section">
        <h3>Connect with Quran</h3>
        {connectWithQuran.map((quranItem, index) => (
          <div key={index} className="quran-item">
            <p><strong>Verse:</strong> <RenderMarkdown markdown={safeguardValue(quranItem.verse)} /></p>
            <p><strong>Translation:</strong> <RenderMarkdown markdown={safeguardValue(quranItem.translation)} /></p>
            <p><strong>Explanation:</strong> <RenderMarkdown markdown={safeguardValue(quranItem.explanation)} /></p>
          </div>
        ))}
      </div>
    )}

    {connectWithHadith && connectWithHadith.length > 0 && (
      <div className="content-section">
        <h3>Connect with Hadith</h3>
        {connectWithHadith.map((hadithItem, index) => (
          <div key={index} className="hadith-item">
            <p><strong>Hadith:</strong> <RenderMarkdown markdown={safeguardValue(hadithItem.hadith)} /></p>
            <p><strong>Explanation:</strong> <RenderMarkdown markdown={safeguardValue(hadithItem.explanation)} /></p>
          </div>
        ))}
      </div>
    )}

    {dua && (
      <div className="content-section">
        <h3>Dua</h3>
        <div className="markdown-content">
          <RenderMarkdown markdown={safeguardValue(dua)} />
        </div>
      </div>
    )}
  </div>
);


export default SlideContent;
